"use client";

import { CounterButton } from "@/src/components/CounterButton";
import { WebShopIcon } from "@/src/components/WebShopIcon";
import useToggle from "@/src/hooks/useToggle";
import { cn } from "@/src/shared/utils/mergeTwClasses";
import { Autocomplete, IconButton, TextField } from "@mui/material";
import clsx from "clsx";
import { useMemo } from "react";
import { FieldValues, useController } from "react-hook-form";
import { SelectCounterFieldOptionType, SelectCounterFieldProps } from "../lib/type";
import { has, isArray, isObject } from "lodash";
import {validationRules} from "@/src/shared/utils/validationRules";

export const SelectCounterField = <T extends FieldValues>({
    control,
    name,
    label,
    options,
    noOptionsText = "На жаль, за вашим запитом нічого не знайдено",
    placeholder,
    hintIcon,
    className,
    maxCounter = 10,
    toggleHintArea,
    closeHintArea,
    openHintArea,
    required,
    disabled,
    readOnly,
    deps,
    ...rest
}: SelectCounterFieldProps<T>) => {

    const {
        field: { onChange, value: controllerValue, ref },
        fieldState: { error: fieldError },
    } = useController({
        control,
        name,
        rules: validationRules.UserSelect({ required, disabled, deps }),
    });

    const optionsMap = useMemo(() => {
        if (isArray(options)) {
            return options.reduce(
                (acc, item) => {
                    acc[item.value] = item.counter;
                    return acc;
                },
                {} as Record<string, SelectCounterFieldOptionType>,
            );
        }
        return {};
    }, [options]);

    const valueMap = { ...optionsMap, ...controllerValue };

    const totalCount = useMemo(() => {
        return Object.values<number>(valueMap).reduce((acc, val) => acc + val, 0);
      }, [valueMap]);

    const handleChange = ({ value, counter }: SelectCounterFieldOptionType) => {
        const updatedMap = { ...valueMap, [value]: counter }
        onChange(updatedMap);
    };

    const {
        isToggled: isSelectCounterOpen,
        close: closeSelectCounter,
        toggle: toggleSelectCounter,
    } = useToggle({
        initialState: false,
    });

    return (
        <Autocomplete
            disablePortal
            disableClearable
            {...rest}
            multiple={undefined}
            noOptionsText={noOptionsText}
            options={options}
            getOptionLabel={(option) => isObject(option) ? option.label : option}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            disableCloseOnSelect
            open={isSelectCounterOpen}
            onOpen={toggleSelectCounter}
            onClose={closeSelectCounter}
            popupIcon={null}
            disabled={disabled}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    error={!!fieldError}
                    disabled={disabled}
                    placeholder={placeholder}
                    helperText={fieldError?.message}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {hintIcon && (
                                    <IconButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            closeSelectCounter();
                                            toggleHintArea?.();
                                        }}
                                        className={cn(
                                           'px-2',
                                            {
                                            hidden: !hintIcon,
                                            flex: hintIcon,
                                            "md:hidden": true,
                                        })}
                                    >
                                        <WebShopIcon name={"questionCircle"} />
                                    </IconButton>
                                )}
                                <IconButton
                                    className={cn(
                                        'px-2',
                                        {
                                        hidden: hintIcon,
                                        flex: !hintIcon,
                                        "md:flex": true,
                                    })}
                                    disabled={disabled}
                                    sx={{
                                        transform: isSelectCounterOpen
                                            ? "rotate(180deg)"
                                            : "rotate(0deg)",
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleSelectCounter();
                                    }}
                                >
                                    <WebShopIcon name={"arrowDown"} />
                                </IconButton>
                            </>
                        ),
                        disableUnderline: true,
                    }}
                    inputProps={{
                        ...params.inputProps,
                        value: `${totalCount === 0 ? "" : totalCount}`,
                        readOnly: true,
                    }}
                />
            )}
            renderOption={(props, option) => {
                const currentCount = (has(valueMap, option?.value) ? valueMap[option.value] : option.counter) ?? 0;
                return (
                    <li
                        {...props}
                        onClick={(e) => e.stopPropagation()}
                        className={cn(props.className, "justify-between bg-white flex-center-row")}
                        key={option.value}
                    >
                        {option.label}
                        <CounterButton
                            key={option.value}
                            className={clsx({
                                "bg-super-light-blue": currentCount > 0,
                                "bg-super-light-gray": currentCount === 0,
                            })}
                            onDecrement={(e) => {
                                e.stopPropagation();
                                if (currentCount > 0) {
                                    handleChange({ ...option, counter: currentCount - 1 });
                                }
                            }}
                            onIncrement={(e) => {
                                e.stopPropagation();
                                if (totalCount < maxCounter) {
                                    handleChange({ ...option, counter: currentCount + 1 });
                                }
                            }}
                            currentCount={currentCount}
                        />
                    </li>
                );
            }}
            className={cn("group", className)}
            onChange={() => {}}
            ref={ref}
        />
    );
};
